import React, { useState } from 'react';
import classNames from 'classnames';

import useStore from 'Hooks/useStore';
import { useUserDisplayNamesStore } from 'Global/User/UserDisplayNameStore';
import alertToast from 'Util/ToastifyUtils';
import { formatStandardDateTime } from 'Util/_HumanWritten/DateUtils';
import { applyDefaultPricingToUsers } from 'Services/Api/_HumanWritten/DefaultPricingApi';
import { Button, Colors, Display, Sizes } from 'Views/Components/Button/Button';
import { newConfirmModal } from 'Views/Components/Modal/NewConfirmModal';
import { PendingUserTypeRequest } from 'Services/Api/_HumanWritten/UserTypeRequestService';
import { usePendingUserTypeRequests } from '../Hooks/usePendingUserTypeRequests';
import { useRequestableUserTypes } from '../Hooks/useRequestableUserTypes';
import { useResolveUserTypeRequest, UserTypeRequestResolution } from '../Hooks/useResolveUserTypeRequest';
import { RequestUserTypeModal } from './RequestUserTypeModal';
import { getUserTypeRequestText } from '../Utils/userTypeRequestText';

interface RequestUserTypeButtonProps {
	/** The customer the request is about. Omit when the logged-in user requests for themselves. */
	userId?: string;
	/** The account's current type, so it isn't offered as an option. */
	currentUserDisplayNameId?: string | null;
	/** True when rendered on the staff view of a customer's account. */
	staffView?: boolean;
	/**
	 * Called after a manager accepts the pending request — accepting assigns the type
	 * server-side, so the page should refetch the user.
	 */
	onAccepted?: () => void;
}

/**
 * Self-contained entry point for the account-type request flow. Who sees what:
 * - Customer on their own account: request button, or the pending state with Cancel.
 *   Hidden entirely for staff+ viewing their own account — they don't request types.
 * - Staff (non-manager) on a customer's account: request-on-behalf button, or the pending
 *   state with Cancel.
 * - Manager+ on a customer's account: the pending state with Accept, Reject and Cancel —
 *   Accept assigns the requested type (they can also assign a type directly through Edit,
 *   which auto-fulfils a matching request).
 */
export function RequestUserTypeButton({
	userId,
	currentUserDisplayNameId,
	staffView = false,
	onAccepted,
}: RequestUserTypeButtonProps) {
	const store = useStore();
	const { userDisplayNames } = useUserDisplayNamesStore();
	const [modalOpen, setModalOpen] = useState(false);
	const requestableTypes = useRequestableUserTypes(currentUserDisplayNameId);
	// Staff+ never request a type on their own account, so skip the pending lookup too.
	const isOwnStaffAccount = !staffView && store.isStaff;
	const { data: pendingRequests, isLoading } = usePendingUserTypeRequests(userId, !isOwnStaffAccount);
	const resolveMutation = useResolveUserTypeRequest(userId);

	if (isOwnStaffAccount) {
		return null;
	}

	const confirmResolve = (pending: PendingUserTypeRequest, action: UserTypeRequestResolution) => {
		const titles: Record<UserTypeRequestResolution, string> = {
			cancel: 'Cancel Account Type Request',
			reject: 'Reject Account Type Request',
			accept: 'Accept Account Type Request',
		};
		const confirmTexts: Record<UserTypeRequestResolution, string> = {
			cancel: 'Cancel request',
			reject: 'Reject request',
			accept: 'Accept request',
		};
		newConfirmModal(
			titles[action],
			<p>
				Are you sure you want to {action} the request for the <strong>{pending.userDisplayName}</strong> account
				type?
				{action === 'accept' && ' The account type will be assigned to this user.'}
			</p>,
			{
				confirmText: confirmTexts[action],
				cancelText: action === 'accept' ? 'Go back' : 'Keep request',
			},
		)
			.then(confirmed => {
				if (!confirmed) return;
				resolveMutation.mutate(
					{ requestId: pending.id, action },
					{
						onSuccess: async () => {
							if (action !== 'accept') return;
							await maybeApplyDefaultPricing(pending);
							onAccepted?.();
						},
					},
				);
			})
			.catch(() => {});
	};

	// After a request is accepted (which assigns the type server-side), offer to apply the type's
	// linked default pricing. No form is mounted in this flow, so persist via the apply-to-users
	// endpoint, then let onAccepted refetch so the new prices show.
	const maybeApplyDefaultPricing = async (pending: PendingUserTypeRequest) => {
		const type = userDisplayNames.find(x => x.id === pending.userDisplayNameId);
		if (!userId || !type?.defaultPricingId) {
			return;
		}
		const applyDefault = await newConfirmModal(
			'Apply default pricing?',
			<p>
				The <strong>{type.displayName}</strong> type has default pricing linked. Apply it to this customer now?
				This replaces their current prices and add-on prices.
			</p>,
			{
				confirmText: 'Apply default pricing',
				cancelText: "Don't apply",
				resolveOnCancel: true,
			},
		).catch(() => false);

		if (applyDefault) {
			try {
				await applyDefaultPricingToUsers(type.defaultPricingId, [userId]);
			} catch {
				alertToast('Could not apply default pricing', 'error');
			}
		}
	};

	const pending = pendingRequests?.[0];
	if (pending) {
		const pillClasses =
			'inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 ' + 'text-sm text-slate-600';
		const requestedAt = formatStandardDateTime(new Date(pending.created)) ?? '';
		const canApprove = staffView && store.can('ApproveUserTypeRequests');
		// Three bare text buttons wrapped unevenly on a phone and read as unrelated links. So: the pill
		// on its own row, then a proper button group — Accept/Reject side by side at equal width, with
		// Cancel (the least consequential) full-width beneath. On desktop they all sit in one row.
		const groupedButtonClass = 'w-full md:w-auto';
		return (
			<div className="flex w-full flex-col items-start gap-3 md:w-auto md:flex-row md:flex-wrap md:items-center">
				<div className={pillClasses} data-test-id="request-user-type-pending">
					<span className="material-symbols-outlined text-base leading-none">schedule</span>
					<span>{`${pending.userDisplayName} requested ${requestedAt}`.trim()}</span>
				</div>
				<div className="grid w-full grid-cols-2 gap-2 md:flex md:w-auto md:items-center">
					{canApprove && (
						<>
							<Button
								display={Display.Solid}
								colors={Colors.Primary}
								sizes={Sizes.Small}
								className={groupedButtonClass}
								newStyles
								onClick={() => confirmResolve(pending, 'accept')}
								disabled={resolveMutation.isPending}
								buttonProps={{ 'data-test-id': 'request-user-type-accept' } as any}
							>
								Accept request
							</Button>
							<Button
								display={Display.Outline}
								colors={Colors.Error}
								sizes={Sizes.Small}
								className={groupedButtonClass}
								newStyles
								onClick={() => confirmResolve(pending, 'reject')}
								disabled={resolveMutation.isPending}
								buttonProps={{ 'data-test-id': 'request-user-type-reject' } as any}
							>
								Reject request
							</Button>
						</>
					)}
					<Button
						display={canApprove ? Display.Text : Display.Outline}
						colors={canApprove ? Colors.Black : Colors.Secondary}
						sizes={Sizes.Small}
						className={classNames('col-span-2', groupedButtonClass)}
						newStyles
						onClick={() => confirmResolve(pending, 'cancel')}
						disabled={resolveMutation.isPending}
						buttonProps={{ 'data-test-id': 'request-user-type-cancel' } as any}
					>
						Cancel request
					</Button>
				</div>
			</div>
		);
	}

	// Whoever can approve requests assigns the type directly (which auto-fulfils a matching pending
	// request), so only customers and staff without ApproveUserTypeRequests get the request affordance.
	const canRequest = staffView ? store.isStaff && !store.can('ApproveUserTypeRequests') : true;
	if (isLoading || !canRequest || requestableTypes.length === 0) {
		return null;
	}

	return (
		<>
			<Button
				display={Display.Outline}
				colors={Colors.Primary}
				newStyles
				onClick={() => setModalOpen(true)}
				buttonProps={{ 'data-test-id': 'request-user-type-button' } as any}
			>
				{/* Shares the modal's title key — no type is selected yet, so no per-type override applies. */}
				{getUserTypeRequestText('title')}
			</Button>
			<RequestUserTypeModal
				userId={userId}
				requestableTypes={requestableTypes}
				isOpen={modalOpen}
				onClose={() => setModalOpen(false)}
			/>
		</>
	);
}
